import { createStyled } from '@lib/style/createStyled';
import { prop } from '@lib/style/theme';
import { unitStrict } from '@lib/style/unit';
import { FlexDirection, JustifyAlignValue } from '@lib/style/utils';
import Grid, { GridProps } from '@material-ui/core/Grid';
import { createMemo } from '@packages/react';
import { applyRef } from '@packages/react/applyRef';
import * as React from 'react';
import { forwardRef } from 'react';
import { classNames, getJustifyAlign } from '../../lib/style/utils';

export interface ColProps extends Omit<GridProps, 'justify'> {
    padding?: any;
    justify?: JustifyAlignValue;
    align?: JustifyAlignValue;
    maxWidth?: string | number;
    direction?: FlexDirection;
    layout?: FlexDirection | string;
    container?: boolean;
}

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Col
 * @date:  18.09.2018
 * @time:  00:53
 * @revisions:
 *  - Dumitrana Alinus [08.08.2019 14:03]: Better Support for SSR & IDE syntax
 *  - Dumitrana Alinus [01.11.2019 03:30]: TS support
 */

export const Col = createMemo<ColProps>(
    forwardRef((props$, ref): React.ReactElement => {
        const { align, padding, maxWidth, layout, ...rest } = props$;

        let align$ = align || rest.alignContent || rest.alignItems;

        if (rest.justify) {
            rest.justify = getJustifyAlign(rest.justify) as any;
        }

        if (align$) {
            align$ = getJustifyAlign(align$);
        }

        rest.direction = rest.direction || (layout as any);

        if (rest.justify || align$ || rest.direction) {
            rest.container = true;
        }

        rest.alignContent = align$ as any;
        rest.alignItems = align$ as any;
        rest.className = classNames(rest.className, 'ui--col');

        rest.style = rest.style || {};

        if (!rest.style.hasOwnProperty('padding')) {
            rest.style.padding = unitStrict(typeof padding !== 'undefined' ? padding : '0 14px');
        }

        if (typeof maxWidth !== 'undefined') {
            rest.style.maxWidth = unitStrict(typeof maxWidth !== 'undefined' ? maxWidth : '100%');
        }

        applyRef(rest, ref);

        return <Grid item {...(rest as any)} />;
    }),
    {
        displayName: 'Col',
        defaultProps: {
            layout: 'row',
        },
    },
);

export const AutoCol = createStyled(
    Col,
    ['flex'],
    ({ css }) => css`
        && {
            flex: ${prop('flex', '1 1 auto')};
        }
    `,
);
